import React, { useState, useCallback, useEffect } from 'react'
import { connect } from 'react-redux'
import { withNavigation, NavigationProps } from '@components/navigation/navigation'
import { withService } from '@shared/hocs/with-service'
import { withPermissions } from '@shared/hocs/with-permissions'
import { Organization } from '@logic'
import { HttpClient } from '@src/service'
import { BuiltInPermission } from '@shared/helpers/constants'
import { Paths } from '@logic'
import { useGoogleDriveConnection } from '../hooks/useGoogleDriveConnection'
import { GoogleDriveConnectionButton } from '../components/GoogleDriveConnectionButton'
import { SyncConfigurationPanel } from '../components/SyncConfigurationPanel'
import { SyncHistoryPanel } from '../components/SyncHistoryPanel'
import { AddSyncConfigModal } from '../components/AddSyncConfigModal'
import { GoogleDriveAddModal } from '../components/GoogleDriveAddModal'
import {
  GoogleDriveContainer,
  ConnectionCard,
  ConnectionHeader,
  ConnectionTitle,
  ErrorMessage
} from '../style'

interface StateProps {
  currentOrganization: Organization
}

interface ServiceProps {
  service: {
    httpClient: HttpClient
  }
}

type Props = NavigationProps & StateProps & ServiceProps

const GoogleDriveSyncPageComponent: React.FC<Props> = ({
  currentOrganization,
  service,
  navigate
}) => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [isDriveLinkModalOpen, setIsDriveLinkModalOpen] = useState(false)
  const [showHistory, setShowHistory] = useState(false)

  const {
    connection,
    folders,
    syncHistory,
    isLoading,
    isSyncing,
    error,
    initiateConnect,
    disconnect,
    fetchFolders,
    addSyncConfig,
    updateConfig,
    deleteConfig,
    startSync,
    fetchHistory,
    completeConnect,
    resolveUrl,
    linkItems,
    importItems
  } = useGoogleDriveConnection({
    httpClient: service.httpClient,
    organizationId: currentOrganization.id
  })

  // Handle OAuth callback
  useEffect(() => {
    const handleOAuthCallback = async (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return
      if (event.data?.type === 'google-drive-oauth-callback' && event.data?.code) {
        try {
          await completeConnect(event.data.code)
        } catch (err) {
          console.error('OAuth callback error:', err)
        }
      }
    }

    window.addEventListener('message', handleOAuthCallback)
    return () => window.removeEventListener('message', handleOAuthCallback)
  }, [completeConnect])

  // Also check URL for OAuth callback code (fallback)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const code = urlParams.get('code')
    if (code && window.location.pathname.includes('google-drive')) {
      completeConnect(code).then(() => {
        // Clear the code from URL
        window.history.replaceState({}, '', window.location.pathname)
      })
    }
  }, [completeConnect])

  const handleAddSyncConfig = useCallback(async (
    googleFolderId: string,
    googleFolderName: string,
    targetDataRoomPath: string,
    syncSubfolders: boolean
  ) => {
    await addSyncConfig(googleFolderId, googleFolderName, targetDataRoomPath, syncSubfolders)
    setIsAddModalOpen(false)
  }, [addSyncConfig])

  const handleToggleEnabled = useCallback(async (configId: string, enabled: boolean) => {
    await updateConfig(configId, { syncEnabled: enabled })
  }, [updateConfig])

  const handleShowHistory = useCallback(() => {
    fetchHistory()
    setShowHistory(!showHistory)
  }, [fetchHistory, showHistory])

  const handleOpenTargetPath = useCallback((targetPath: string) => {
    const search = targetPath ? `?path=${encodeURIComponent(targetPath)}` : ''
    navigate({ pathname: Paths.organizationDataRoom, search }, { organization: currentOrganization.id })
  }, [currentOrganization.id, navigate])

  return (
    <GoogleDriveContainer>
      <ConnectionCard>
        <ConnectionHeader>
          <ConnectionTitle>Google Drive Sync</ConnectionTitle>
          <GoogleDriveConnectionButton
            isConnected={connection?.connected || false}
            email={connection?.email}
            isLoading={isLoading}
            onConnect={initiateConnect}
            onDisconnect={disconnect}
          />
        </ConnectionHeader>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        <p style={{ color: '#888', marginTop: 0 }}>
          Connect Google Drive, choose files or folders, and keep selected
          Data Room documents synced from Drive. Picker selections use the
          narrow drive.file scope; pasted links are resolved only when the
          connected account has access.
        </p>

        {connection?.connected && (
          <div style={{ color: '#aaa', fontSize: '0.875rem' }}>
            {(connection.syncConfigurations || []).length} synced folder{(connection.syncConfigurations || []).length === 1 ? '' : 's'}
            {' · '}
            {(connection.linkedItems || []).length} linked item{(connection.linkedItems || []).length === 1 ? '' : 's'}
            {' · '}
            Last sync: {connection.lastSyncAt ? new Date(connection.lastSyncAt).toLocaleString() : 'Never'}
          </div>
        )}
      </ConnectionCard>

      {connection?.connected && (
        <>
          <ConnectionCard>
            <ConnectionHeader>
              <ConnectionTitle>Synced Folders and Drive Links</ConnectionTitle>
              <button
                onClick={() => setIsDriveLinkModalOpen(true)}
                style={{
                  background: '#4285f4',
                  border: 'none',
                  color: 'white',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  padding: '10px 16px'
                }}
              >
                Add from Google Drive
              </button>
            </ConnectionHeader>

            <SyncConfigurationPanel
              configurations={connection.syncConfigurations || []}
              isSyncing={isSyncing}
              onAddNew={() => {
                fetchFolders()
                setIsAddModalOpen(true)
              }}
              onDelete={deleteConfig}
              onSync={startSync}
              onToggleEnabled={handleToggleEnabled}
              onOpenTargetPath={handleOpenTargetPath}
            />
          </ConnectionCard>

          <ConnectionCard>
            <ConnectionHeader>
              <ConnectionTitle>Sync History</ConnectionTitle>
              <button
                onClick={handleShowHistory}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#4285f4',
                  cursor: 'pointer',
                  fontSize: '0.875rem'
                }}
              >
                {showHistory ? 'Hide' : 'Show'} History
              </button>
            </ConnectionHeader>

            {showHistory && <SyncHistoryPanel history={syncHistory} />}
          </ConnectionCard>
        </>
      )}

      <AddSyncConfigModal
        isOpen={isAddModalOpen}
        folders={folders}
        isLoadingFolders={isLoading}
        onClose={() => setIsAddModalOpen(false)}
        onFetchFolders={fetchFolders}
        onSave={handleAddSyncConfig}
      />

      <GoogleDriveAddModal
        isOpen={isDriveLinkModalOpen}
        defaultDataRoomPath="/Google Drive"
        isConnected={connection?.connected || false}
        connectedEmail={connection?.email}
        onClose={() => setIsDriveLinkModalOpen(false)}
        onConnect={initiateConnect}
        onResolveUrl={resolveUrl}
        onLinkItems={async (items, targetPath, mirrorStructure) => {
          await linkItems(items, targetPath, mirrorStructure)
        }}
        onImportItems={async (items, targetPath, mirrorStructure) => {
          await importItems(items, targetPath, mirrorStructure)
        }}
        onSyncFolders={async (folders, targetPath, syncSubfolders) => {
          await Promise.all(folders.map(folder => addSyncConfig(folder.id, folder.name || folder.id, targetPath, syncSubfolders)))
        }}
      />
    </GoogleDriveContainer>
  )
}

const mapStateToProps = (state: any): StateProps => ({
  currentOrganization: state.organization?.currentOrganization
})

let _GoogleDriveSyncPage: any = null
export const GoogleDriveSyncPage: React.ComponentType<any> = (props: any) => {
  if (!_GoogleDriveSyncPage) {
    _GoogleDriveSyncPage = withNavigation(
      connect(mapStateToProps)(
        withService(
          withPermissions([BuiltInPermission.editDocuments])(
            GoogleDriveSyncPageComponent
          )
        )
      )
    )
  }
  return React.createElement(_GoogleDriveSyncPage, props)
}
